import React, { useEffect, useState, useCallback } from 'react';
import emailjs from '@emailjs/browser';
import colors from '../../constants/colors';
import twitterIcon from '../../assets/pictures/contact-twitter.png';
import ghIcon from '../../assets/pictures/contact-gh.png';
import inIcon from '../../assets/pictures/contact-in.png';
import ResumeDownload from './ResumeDownload';

export interface ContactProps {}

// function to validate email
const validateEmail = (email: string) => {
    const re =
        // eslint-disable-next-line
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
};

interface SocialBoxProps {
    icon: string;
    link: string;
}

const SocialBox: React.FC<SocialBoxProps> = ({ link, icon }) => {
    return (
        <a rel="noreferrer" target="_blank" href={link}>
            <div className="big-button-container" style={styles.social}>
                <img src={icon} alt="" style={styles.socialImage} />
            </div>
        </a>
    );
};

const Contact: React.FC<ContactProps> = (props) => {
    const [company, setCompany] = useState('');
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [message, setMessage] = useState('');
    const [isFormValid, setIsFormValid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [formMessage, setFormMessage] = useState('');
    const [formMessageColor, setFormMessageColor] = useState('');

    useEffect(() => {
        if (validateEmail(email) && name.length > 0 && message.length > 0) {
            setIsFormValid(true);
        } else {
            setIsFormValid(false);
        }
    }, [email, name, message]);

    async function submitForm() {
        if (!isFormValid) {
            setFormMessage('Modulo non verificabile, riprovare.');
            setFormMessageColor('red');
            return;
        }
        const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || '';
        const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '';
        const clientTemplateId =
            process.env.REACT_APP_EMAILJS_TEMPLATE_ID_CLIENT || templateId;
        const ownerTemplateId =
            process.env.REACT_APP_EMAILJS_TEMPLATE_ID_OWNER || '';
        const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '';

        if (!serviceId || !templateId || !publicKey) {
            setFormMessage(
                'Configurazione EmailJS mancante. Imposta REACT_APP_EMAILJS_SERVICE_ID, REACT_APP_EMAILJS_TEMPLATE_ID e REACT_APP_EMAILJS_PUBLIC_KEY.'
            );
            setFormMessageColor(colors.red);
            return;
        }

        try {
            setIsLoading(true);
            const baseParams = { company, email, name, message };
            const ownerSummary = `Nuova compilazione form\n\nNome: ${name}\nEmail: ${email}\nCompany: ${company || '-'}\nMessaggio: ${message}`;
            // 1) Conferma al cliente
            const clientParams = {
                ...baseParams,
                to_email: email,
                subject: 'Ho ricevuto il tuo messaggio',
            };
            await emailjs.send(
                serviceId,
                clientTemplateId,
                clientParams,
                publicKey
            );

            // 2) Notifica al proprietario (usa template dedicato se presente, altrimenti riusa quello del cliente)
            const ownerIdToUse = ownerTemplateId || clientTemplateId;
            const ownerParams = {
                ...baseParams,
                to_email: 'marangonidavide05@gmail.com',
                // Copriamo template che usano `email` come destinatario
                email: 'marangonidavide05@gmail.com',
                // consente di rispondere direttamente al cliente
                reply_to: email,
                // disponibile per uso nel body del template se desiderato
                customer_email: email,
                // soggetto e riepilogo completo
                subject: 'Nuova compilazione form',
                summary: ownerSummary,
            };
            await emailjs.send(
                serviceId,
                ownerIdToUse,
                ownerParams,
                publicKey
            );

            setFormMessage(`Messaggio inviato con successo. Grazie ${name}!`);
            setCompany('');
            setEmail('');
            setName('');
            setMessage('');
            setFormMessageColor(colors.blue);
        } catch (e) {
            setFormMessage(
                'Si è verificato un errore durante l\'invio del messaggio. Riprova.'
            );
            setFormMessageColor(colors.red);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (formMessage.length > 0) {
            setTimeout(() => {
                setFormMessage('');
                setFormMessageColor('');
            }, 4000);
        }
    }, [formMessage]);

    return (
        <div className="site-page-content">
            <div style={styles.header}>
                <h1>Contact</h1>
                <div style={styles.socials}>

                    <SocialBox
                        icon={inIcon}
                        link={'https://www.linkedin.com/in/davide-marangoni-a08913255/'}
                    />

                </div>
            </div>
            <div className="text-block">
                <p>
                    Non aspetto altro che il tuo progetto, non esitate a contattarmi - 
                    Potete raggiungermi tramite la mia email personale, o
                    compilare il modulo sottostante!
                </p>
                <br />
                <p>
                    <b>Email: </b>
                    <a href="mailto:marangonidavide05@gmail.com">
                        marangonidavide05@gmail.com
                    </a>
                </p>

                <div style={styles.form}>
                    <label>
                        <p>
                            {!name && <span style={styles.star}>*</span>}
                            <b>Your name:</b>
                        </p>
                    </label>
                    <input
                        style={styles.formItem}
                        type="text"
                        name="name"
                        placeholder="Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <label>
                        <p>
                            {!validateEmail(email) && (
                                <span style={styles.star}>*</span>
                            )}
                            <b>Email:</b>
                        </p>
                    </label>
                    <input
                        style={styles.formItem}
                        type="email"
                        name="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                    <label>
                        <p>
                            <b>Company (optional):</b>
                        </p>
                    </label>
                    <input
                        style={styles.formItem}
                        type="company"
                        name="company"
                        placeholder="Company"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                    />
                    <label>
                        <p>
                            {!message && <span style={styles.star}>*</span>}
                            <b>Message:</b>
                        </p>
                    </label>
                    <textarea
                        name="message"
                        placeholder="Message"
                        style={styles.formItem}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                    />
                    <div style={styles.buttons}>
                        <button
                            className="site-button"
                            style={styles.button}
                            type="submit"
                            disabled={!isFormValid || isLoading}
                            onMouseDown={submitForm}
                        >
                            {!isLoading ? (
                                'Send Message'
                            ) : (
                                <p className="loading">Sending</p>
                            )}
                        </button>
                        <div style={styles.formInfo}>
                            <p
                                style={Object.assign(
                                    {},
                                    { color: formMessageColor }
                                )}
                            >
                                <b>
                                    <sub>
                                        {formMessage
                                            ? `${formMessage}`
                                            : ' Tutti i messaggi vengono inoltrati direttamente alla mia email personale'}
                                    </sub>
                                </b>
                            </p>
                            <p>
                                <sub>
                                    {!isFormValid ? (
                                        <span>
                                            <b style={styles.star}>*</b> =
                                            required
                                        </span>
                                    ) : (
                                        '\xa0'
                                    )}
                                </sub>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <ResumeDownload altText="Vuoi una copia del mio curriculum?" />
        </div>
    );
};

const styles: StyleSheetCSS = {
    form: {
        flexDirection: 'column',
        marginTop: 32,
    },
    formItem: {
        marginTop: 4,
        marginBottom: 16,
    },
    socialImage: {
        width: 36,
        height: 36,
    },
    buttons: {
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    formInfo: {
        textAlign: 'right',

        flexDirection: 'column',
        alignItems: 'flex-end',
        paddingLeft: 24,
    },
    star: {
        paddingRight: 4,
        color: 'red',
    },
    button: {
        minWidth: 184,
        height: 32,
    },
    header: {
        alignItems: 'flex-end',
        justifyContent: 'space-between',
    },
    socials: {
        marginBottom: 16,
        justifyContent: 'flex-end',
    },
    social: {
        width: 4,
        height: 4,
        // borderRadius: 1000,

        justifyContent: 'center',
        alignItems: 'center',
        marginLeft: 8,
    },
};

export default Contact;
